import { useState } from 'react';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';

const s = {
  field: { marginBottom: '14px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#8FA4B5',
    marginBottom: '5px',
  },
  required: { color: '#E05555', marginLeft: '2px' },
  optionalHint: { color: '#4C5C68', fontSize: '12px', marginLeft: '4px' },
  input: {
    display: 'block',
    width: '100%',
    backgroundColor: '#0a1929',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '5px',
    padding: '7px 10px',
    fontSize: '13px',
    color: '#E8EDF2',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  inputError: { borderColor: '#E05555' },
  errorMsg: { marginTop: '3px', fontSize: '11px', color: '#E05555' },
  footer: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' },
  btnCancel: {
    padding: '7px 14px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '5px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  btnSave: {
    padding: '7px 14px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '5px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

interface ApplicationFormProps {
  initialName?: string;
  initialDescription?: string;
  onSave: (name: string, description?: string) => void;
  onCancel: () => void;
  submitLabel?: string;
}

export function ApplicationForm({
  initialName = '',
  initialDescription = '',
  onSave,
  onCancel,
  submitLabel = 'Add Application',
}: ApplicationFormProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [nameError, setNameError] = useState('');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Dirty if the user has changed either field from the initial values.
  const isDirty = name !== initialName || description !== initialDescription;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setNameError('Application name is required'); return; }
    onSave(name.trim(), description.trim() || undefined);
  }

  function handleCancel() {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      onCancel();
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div style={s.field}>
          <label style={s.label}>Name<span style={s.required}>*</span></label>
          <input
            type="text"
            value={name}
            autoFocus
            placeholder="e.g. Clinical Information System"
            onChange={e => { setName(e.target.value); setNameError(''); }}
            style={{ ...s.input, ...(nameError ? s.inputError : {}) }}
          />
          {nameError && <p style={s.errorMsg}>{nameError}</p>}
        </div>

        <div style={s.field}>
          <label style={s.label}>Description<span style={s.optionalHint}>(optional)</span></label>
          <input
            type="text"
            value={description}
            placeholder="Short description of this application group"
            onChange={e => setDescription(e.target.value)}
            style={s.input}
          />
        </div>

        <div style={s.footer}>
          <button type="button" style={s.btnCancel} onClick={handleCancel}>Cancel</button>
          <button type="submit" style={s.btnSave}>{submitLabel}</button>
        </div>
      </form>

      <UnsavedChangesDialog
        open={showDiscardDialog}
        onStay={() => setShowDiscardDialog(false)}
        onLeave={onCancel}
      />
    </>
  );
}
